import { useCallback, useMemo } from 'react';
import {
  RelatedDocumentsDto,
  TableConfigDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../../store';
import {
  selectFullscreenPageSidebarRowIdToHighlight,
  selectPageSidebarRowIdToHighlight,
} from '../../../store/states/ui';
import type {
  PageSidebarActiveContent,
  PageSidebarScope,
} from '../../page-sidebar/types';

export type TableSidebarRowData = {
  rowId: string;
  data: TableRowDto['data'];
};

export type TableSidebarLoaderProps = {
  data: TableSidebarRowData | null;
  fileDownloadColumnKeys: string[];
  opened: boolean;
  relatedDocumentsConfig?: RelatedDocumentsDto;
  showWorkflowFiles: boolean;
};

type UseTableSidebarPropertiesReturnValue = {
  rowIdToHighlight?: string;
  toggle?: (data: TableSidebarRowData) => void;
};

type UseTableSidebarPropertiesOptions = {
  scope?: PageSidebarScope;
};

export function useTableSidebarProperties(
  widgetId: string,
  config: TableConfigDto,
  { scope = 'page' }: UseTableSidebarPropertiesOptions = {}
): UseTableSidebarPropertiesReturnValue {
  const [openPageSidebar, rowIdToHighlight] = useStore(
    useShallow((state) => [
      state.openPageSidebar,
      scope === 'fullscreen'
        ? selectFullscreenPageSidebarRowIdToHighlight(state, widgetId)
        : selectPageSidebarRowIdToHighlight(state, widgetId),
    ])
  );

  const { showWorkflowFiles, fileDownloadColumnKeys } = useMemo(() => {
    let showWorkflowFiles = false;
    const fileDownloadColumnKeys: string[] = [];

    for (const column of config.columns) {
      if (column.type === 'workflowVisualization') {
        showWorkflowFiles = true;
      }
      if (column.type === 'fileDownload') {
        fileDownloadColumnKeys.push(column.key);
      }
    }

    return { showWorkflowFiles, fileDownloadColumnKeys };
  }, [config.columns]);

  const hasSidebar = useMemo(
    () =>
      config.cacheKey.dataSourceType === 'datatable'
        ? config.relatedDocuments != null ||
          showWorkflowFiles ||
          fileDownloadColumnKeys.length > 0
        : config.enableDocviewSidebar,
    [
      config.cacheKey.dataSourceType,
      config.enableDocviewSidebar,
      config.relatedDocuments,
      showWorkflowFiles,
      fileDownloadColumnKeys,
    ]
  );

  const toggleSidebarCb = useCallback(
    (rowData: TableSidebarRowData) => {
      const content: PageSidebarActiveContent = {
        data: rowData,
        fileDownloadColumnKeys,
        relatedDocumentsConfig: config.relatedDocuments,
        showWorkflowFiles,
        sideBarType: config.cacheKey.dataSourceType,
        widgetId,
      };
      openPageSidebar(scope, content);
    },
    [
      config.cacheKey.dataSourceType,
      config.relatedDocuments,
      fileDownloadColumnKeys,
      openPageSidebar,
      scope,
      showWorkflowFiles,
      widgetId,
    ]
  );

  if (!hasSidebar) {
    return {};
  }

  return {
    rowIdToHighlight,
    toggle: toggleSidebarCb,
  };
}
